import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TeamProperties from './TeamProperties';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import './Teams.scss';
import '../../../styles/GenericSiteStyles.scss';
import { TeamDataProvider, Team } from '../../../data/TeamDataProvider';
import { UserRoles } from '../../../constants/UserRoles';

const Teams: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [teams, setTeams] = useState<Team[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();
  const teamDataProvider = React.useMemo(() => new TeamDataProvider(), []);

  // Load teams from API
  useEffect(() => {
    teamDataProvider.getAll().then(setTeams);
  }, []);

  const columns: DetailedListColumn<Team>[] = useMemo(() => [
    { key: 'name', header: t('common:name'), type: 'string', width: 200 },
    { key: 'description', header: t('common:description'), type: 'string', width: 500 },
  ], [t]);

  const handleChange = async (_items: Team[], action: 'add' | 'edit' | 'delete', changedItem: Team) => {
    if (action === 'delete' && changedItem.id) {
      try {
        await teamDataProvider.delete(changedItem.id);
      } catch {
        // Revert the optimistic removal by reloading from the server
        teamDataProvider.getAll().then(setTeams);
      }
    }
  };

  const handleSave = async (team: { id?: string; name: string; description?: string }) => {
    if (team.id) {
      await teamDataProvider.update(team.id, team);
      setTeams(prev => prev.map(t => t.id === team.id ? { ...t, ...team } : t));
    } else {
      const created = await teamDataProvider.create(team);
      setTeams(prev => [...prev, created]);
    }
    setShowCreate(false);
  };

  return (
    <section className="teams-page">
      <MainScreenHeader title={t('screens:teams.title')} />
      <DetailedListComponent<Team>
        columns={columns}
        dataSource={{ data: teams, setData: setTeams }}
        rowKey={item => item.id ?? ''}
        addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
        editRole=""
        deleteRole={`${UserRoles.Admin},${UserRoles.Manager}`}
        onEditRequest={item => navigate(`/teams/${item.id}`)}
        onAddRequest={() => setShowCreate(true)}
        onChange={handleChange}
        actionsColumnWidth={100}
        actionsColumnFixed
      />
      {showCreate && (
        <TeamProperties
          onClose={() => setShowCreate(false)}
          onSave={handleSave}
        />
      )}
    </section>
  );
};

export default Teams;
